import { api, downloadFile } from '@/shared/api/client'
import { qs, type QueryValue } from '@/shared/lib/query'

/**
 * The Incident Desk's server calls, in one place.
 *
 * ## Why `api.*` and never a bare `fetch`
 *
 * The bearer token is attached by `shared/api/client`, not by `fetch`, and the client
 * turns an RFC 9457 problem body into a thrown `ApiError` that `explain()` renders. A raw
 * `fetch` here would work in the browser, work in mock mode — `mock-api.ts` replaces
 * `window.fetch`, so the interceptor catches it — and fail against the real server as a
 * 401 with an empty screen and no message. Station-mates shipped that way once; this file
 * exists partly so it does not happen twice.
 *
 * ## Hand-written types
 *
 * Rather than reaching into `openapi/schema.d.ts`, where every shape is nested behind
 * `components['schemas']`. Every other feature here does the same. The shapes below mirror
 * the DTO records in `ke.zurimate.axis.incident.dto` field for field, including the
 * server-resolved `*Name` fields — a client that resolves ids itself is a client that
 * makes N+1 calls the server already batched.
 */

// ── shapes ─────────────────────────────────────────────────────────────────

/** `GET /incidents` — the register row. Deliberately lighter than the detail. */
export interface IncidentSummary {
  id: string
  ref: string
  title: string
  subcategory: string | null
  categoryCode: string
  severityCode: string
  priorityCode: string
  /** Where it is in the WORKFLOW. Not how much we believe it — that is `verificationCode`. */
  statusCode: string
  /** How much we BELIEVE it. An incident can be RESOLVED and still UNVERIFIED. */
  verificationCode: string
  unitId: string | null
  /** Resolved server-side; null if the unit was dissolved or none was named. */
  unitName: string | null
  occurredAt: string | null
  reportedAt: string
  deathCount: number | null
  injuryCount: number | null
  evidenceCount: number
  hasLocation: boolean
  assignedTo: string | null
  assignedToName: string | null
}

/** `GET /incidents/{id}` — everything the drawer and the wizard's review step read. */
export interface Incident {
  id: string
  ref: string
  categoryCode: string
  subcategory: string | null
  title: string
  description: string | null
  severityCode: string
  priorityCode: string
  statusCode: string
  verificationCode: string
  verificationNote: string | null
  unitId: string | null
  unitName: string | null
  unitPath: string | null
  locationLabel: string | null
  latitude: number | null
  longitude: number | null
  /** DEVICE_GPS | MAP_PICK | UNIT_CENTROID | NONE — how the coordinate was obtained. */
  locationSourceCode: string | null
  locationAccuracyM: number | null
  locationAbsentReason: string | null
  occurredAt: string | null
  occurredAtPrecisionCode: string
  reportedAt: string
  acknowledgedAt: string | null
  escalatedAt: string | null
  resolvedAt: string | null
  closedAt: string | null
  reportedBy: string | null
  /** Masked to initials unless the caller holds `incident:read-sensitive`. */
  reportedByName: string | null
  assignedTo: string | null
  assignedToName: string | null
  escalatedTo: string | null
  victimCount: number | null
  injuryCount: number | null
  deathCount: number | null
  arrestsMade: number | null
  estimatedVoterImpact: number | null
  perpetrator: string | null
  perpetratorPartyId: string | null
  relatedCandidateId: string | null
  relatedPartyId: string | null
  sourceOutlet: string | null
  sourceUrl: string | null
  sourcePublishedAt: string | null
  mediaCoverageCode: string
  iebcComplaintRef: string | null
  dciCaseRef: string | null
  courtCaseRef: string | null
  ncicRef: string | null
  resolutionCode: string | null
  resolutionNote: string | null
  electionId: string | null
  evidenceCount: number
  openActionCount: number
  liveFeedCount: number
  tags: string[]
  createdAt: string
  updatedAt: string | null
}

export interface TimelineEntry {
  id: string
  incidentId: string
  actionCode: string
  fromValue: string | null
  toValue: string | null
  note: string | null
  actorId: string | null
  actorName: string | null
  createdAt: string
}

export interface Evidence {
  id: string
  incidentId: string
  kindCode: string
  fileId: string | null
  externalUrl: string | null
  coverageEventId: string | null
  caption: string | null
  capturedAt: string | null
  latitude: number | null
  longitude: number | null
  /** Withdrawal KEEPS the row. Evidence is append-only; a retraction is a new fact. */
  withdrawnAt: string | null
  withdrawnBy: string | null
  withdrawnReason: string | null
  addedBy: string | null
  createdAt: string
  withdrawn: boolean
}

export interface FollowUpAction {
  id: string
  incidentId: string
  description: string
  dueOn: string | null
  assignedTo: string | null
  assignedToName: string | null
  completedAt: string | null
  completedBy: string | null
  createdBy: string | null
  createdAt: string
  open: boolean
}

export interface MapMarker {
  id: string
  ref: string
  latitude: number | null
  longitude: number | null
  severityCode: string
  statusCode: string
  unitId: string | null
  locationLabel: string | null
}

export interface AnalyticsSummary {
  total: number
  open: number
  investigating: number
  escalated: number
  resolved: number
  closed: number
  disputed: number
  draftsMine: number
  bySeverity: Record<string, number>
  byCategory: Record<string, number>
  byStatus: Record<string, number>
  byVerification: Record<string, number>
  byMediaCoverage: Record<string, number>
  totalDeaths: number
  totalInjuries: number
  totalArrests: number
  totalVictims: number
  totalVoterImpact: number
  /** (VERIFIED + CORROBORATED) / total, or null when there is nothing to divide. */
  verifiedShare: number | null
  last7Days: number
  last30Days: number
  /** Null below the server's sample floor — a median over three rows is noise. */
  medianHoursToAcknowledge: number | null
  medianHoursToResolve: number | null
  p90HoursToResolve: number | null
  openOlderThan48h: number
  scopeUnitId: string | null
}

export interface TrendPoint {
  bucket: string
  total: number
  critical: number
}

export interface GeographyRow {
  unitId: string | null
  unitName: string | null
  unitPath: string
  total: number
  critical: number
  deaths: number
}

export interface SourceRow { outlet: string; total: number }
export interface TagRow { tag: string; total: number }

export interface ResponseTimes {
  medianHoursToAcknowledge: number | null
  medianHoursToResolve: number | null
  p90HoursToResolve: number | null
  acknowledgedCount: number
  resolvedCount: number
  series: { bucket: string; medianHoursToResolve: number | null; count: number }[]
}

// ── filters ────────────────────────────────────────────────────────────────

export interface RegisterFilter {
  category?: string | null
  severity?: string | null
  status?: string | null
  verification?: string | null
  unitId?: string | null
  electionId?: string | null
  assignedTo?: string | null
  /** Only incidents I reported or am assigned — includes my own DRAFTs. */
  mine?: boolean
  from?: string | null
  to?: string | null
  search?: string | null
  size?: number
  offset?: number
}

export interface AnalyticsFilter {
  category?: string | null
  severity?: string | null
  status?: string | null
  verification?: string | null
  electionId?: string | null
  /**
   * BOTH OPTIONAL, on every analytics route. A browser cannot know when polling day
   * was; null means unbounded. The turnout hourly-rollup route shipped with these
   * required and was unusable from the UI.
   */
  from?: string | null
  to?: string | null
}

const q = (o: Record<string, QueryValue>) => qs(o)

// ── the register ───────────────────────────────────────────────────────────

export const listIncidents = (f: RegisterFilter) =>
  api.get<IncidentSummary[]>(`/incidents${q({
    category: f.category, severity: f.severity, status: f.status,
    verification: f.verification, unitId: f.unitId, electionId: f.electionId,
    assignedTo: f.assignedTo, mine: f.mine ? true : undefined,
    from: f.from, to: f.to, search: f.search,
    size: f.size, offset: f.offset,
  })}`)

export const getIncident = (id: string) => api.get<Incident>(`/incidents/${id}`)

/** Creates a DRAFT. The reporter comes from the security context, never the body. */
export const createIncident = (body: {
  categoryCode: string
  title: string
  subcategory?: string | null
  description?: string | null
  severityCode?: string | null
  priorityCode?: string | null
  unitId?: string | null
  locationLabel?: string | null
  latitude?: number | null
  longitude?: number | null
  locationSourceCode?: string | null
  locationAccuracyM?: number | null
  locationAbsentReason?: string | null
  occurredAt?: string | null
  occurredAtPrecisionCode?: string | null
  electionId?: string | null
}) => api.post<Incident>('/incidents', body)

/** Every field the wizard can set after step 1. All optional; null clears. */
export type IncidentPatch = Partial<{
  categoryCode: string | null
  subcategory: string | null
  title: string | null
  description: string | null
  severityCode: string | null
  priorityCode: string | null
  unitId: string | null
  locationLabel: string | null
  latitude: number | null
  longitude: number | null
  locationSourceCode: string | null
  locationAccuracyM: number | null
  locationAbsentReason: string | null
  occurredAt: string | null
  occurredAtPrecisionCode: string | null
  victimCount: number | null
  injuryCount: number | null
  deathCount: number | null
  arrestsMade: number | null
  estimatedVoterImpact: number | null
  perpetrator: string | null
  perpetratorPartyId: string | null
  relatedCandidateId: string | null
  relatedPartyId: string | null
  sourceOutlet: string | null
  sourceUrl: string | null
  sourcePublishedAt: string | null
  mediaCoverageCode: string | null
  iebcComplaintRef: string | null
  dciCaseRef: string | null
  courtCaseRef: string | null
  ncicRef: string | null
  electionId: string | null
}>

export const patchIncident = (id: string, body: IncidentPatch) =>
  api.patch<Incident>(`/incidents/${id}`, body)

/** DRAFT + author only. Everything else is CLOSED with a resolution, never deleted. */
export const deleteIncident = (id: string) => api.del<void>(`/incidents/${id}`)

// ── the lifecycle ──────────────────────────────────────────────────────────

export const submitIncident = (id: string) =>
  api.post<Incident>(`/incidents/${id}/submit`)

export const changeStatus = (id: string, statusCode: string, note?: string | null) =>
  api.post<Incident>(`/incidents/${id}/status`, { statusCode, note: note ?? null })

export const assignIncident = (id: string, assigneeUserId: string, note?: string | null) =>
  api.post<Incident>(`/incidents/${id}/assign`, { assigneeUserId, note: note ?? null })

export const acknowledgeIncident = (id: string) =>
  api.post<Incident>(`/incidents/${id}/acknowledge`)

export const escalateIncident = (id: string, escalatedTo: string, note?: string | null) =>
  api.post<Incident>(`/incidents/${id}/escalate`, { escalatedTo, note: note ?? null })

export const verifyIncident = (id: string, verificationCode: string, note?: string | null) =>
  api.post<Incident>(`/incidents/${id}/verify`, { verificationCode, note: note ?? null })

export const resolveIncident = (id: string, resolutionCode: string, note?: string | null) =>
  api.post<Incident>(`/incidents/${id}/resolve`, { resolutionCode, note: note ?? null })

export const closeIncident = (id: string, note?: string | null) =>
  api.post<Incident>(`/incidents/${id}/close`, { note: note ?? null })

export const reopenIncident = (id: string, note?: string | null) =>
  api.post<Incident>(`/incidents/${id}/reopen`, { note: note ?? null })

// ── the record ─────────────────────────────────────────────────────────────

export const getTimeline = (id: string) =>
  api.get<TimelineEntry[]>(`/incidents/${id}/timeline`)

export const addComment = (id: string, note: string) =>
  api.post<TimelineEntry>(`/incidents/${id}/comments`, { note })

export const listEvidence = (id: string) =>
  api.get<Evidence[]>(`/incidents/${id}/evidence`)

export const addEvidence = (id: string, body: {
  kindCode: string
  fileId?: string | null
  externalUrl?: string | null
  coverageEventId?: string | null
  caption?: string | null
  capturedAt?: string | null
  latitude?: number | null
  longitude?: number | null
}) => api.post<Evidence>(`/incidents/${id}/evidence`, body)

/** Keeps the row and hides it from the live list. Nothing a human asserted is deleted. */
export const withdrawEvidence = (id: string, evidenceId: string, reason: string) =>
  api.post<Evidence>(`/incidents/${id}/evidence/${evidenceId}/withdraw`, { reason })

export const getTags = (id: string) => api.get<string[]>(`/incidents/${id}/tags`)
export const putTags = (id: string, tags: string[]) =>
  api.put<string[]>(`/incidents/${id}/tags`, { tags })

export const listActions = (id: string) =>
  api.get<FollowUpAction[]>(`/incidents/${id}/actions`)

export const createAction = (id: string, body: {
  description: string
  dueOn?: string | null
  assignedTo?: string | null
}) => api.post<FollowUpAction>(`/incidents/${id}/actions`, body)

export const completeAction = (id: string, actionId: string, note?: string | null) =>
  api.post<FollowUpAction>(`/incidents/${id}/actions/${actionId}/complete`, { note: note ?? null })

// ── analytics and the map ──────────────────────────────────────────────────

export const analyticsSummary = (f: AnalyticsFilter) =>
  api.get<AnalyticsSummary>(`/incidents/analytics/summary${q({ ...f })}`)

export const analyticsTrend = (f: AnalyticsFilter & { bucket?: 'DAY' | 'WEEK' }) =>
  api.get<TrendPoint[]>(`/incidents/analytics/trend${q({ ...f })}`)

export const analyticsGeography = (
  f: { level?: 'COUNTY' | 'CONSTITUENCY' | 'WARD' } & AnalyticsFilter,
) => api.get<GeographyRow[]>(`/incidents/analytics/geography${q({
  level: f.level, electionId: f.electionId, from: f.from, to: f.to,
})}`)

export const analyticsSources = (f: AnalyticsFilter) =>
  api.get<SourceRow[]>(`/incidents/analytics/sources${q({
    electionId: f.electionId, from: f.from, to: f.to,
  })}`)

export const analyticsTags = (f: AnalyticsFilter) =>
  api.get<TagRow[]>(`/incidents/analytics/tags${q({
    electionId: f.electionId, from: f.from, to: f.to,
  })}`)

export const analyticsResponseTimes = (f: AnalyticsFilter) =>
  api.get<ResponseTimes>(`/incidents/analytics/response-times${q({
    electionId: f.electionId, from: f.from, to: f.to,
  })}`)

export const mapMarkers = (f: RegisterFilter) =>
  api.get<MapMarker[]>(`/incidents/map${q({
    category: f.category, severity: f.severity, status: f.status,
    verification: f.verification, unitId: f.unitId, electionId: f.electionId,
    from: f.from, to: f.to,
  })}`)

/**
 * The register as CSV. Goes through `downloadFile` rather than `api.get`, which parses
 * every body as JSON — right for the rest of this API and wrong for `text/csv`. A failed
 * download still carries a problem body, and `downloadFile` surfaces it.
 */
export const exportIncidents = (f: RegisterFilter) =>
  downloadFile(`/incidents/export${q({
    category: f.category, severity: f.severity, status: f.status,
    verification: f.verification, unitId: f.unitId, electionId: f.electionId,
    assignedTo: f.assignedTo, from: f.from, to: f.to, search: f.search,
  })}`)

// ── vocabulary ─────────────────────────────────────────────────────────────

/**
 * Every `<select>` in this feature is driven by one of these arrays, never by a list
 * written in the client. A hand-written option list that drifts from the server is the
 * defect `VocabularyContractTest` and `mock-route-check.mjs` exist to prevent.
 */
export interface IncidentVocabulary {
  categories: string[]
  severities: string[]
  priorities: string[]
  statuses: string[]
  verifications: string[]
  resolutions: string[]
  mediaCoverages: string[]
  evidenceKinds: string[]
  timelineActions: string[]
  locationSources: string[]
  timePrecisions: string[]
}

const vocab = (name: string) => api.get<string[]>(`/incidents/vocabulary/${name}`)

export async function loadVocabulary(): Promise<IncidentVocabulary> {
  const [
    categories, severities, priorities, statuses, verifications, resolutions,
    mediaCoverages, evidenceKinds, timelineActions, locationSources, timePrecisions,
  ] = await Promise.all([
    vocab('categories'), vocab('severities'), vocab('priorities'), vocab('statuses'),
    vocab('verifications'), vocab('resolutions'), vocab('media-coverages'),
    vocab('evidence-kinds'), vocab('timeline-actions'), vocab('location-sources'),
    vocab('time-precisions'),
  ])
  return {
    categories, severities, priorities, statuses, verifications, resolutions,
    mediaCoverages, evidenceKinds, timelineActions, locationSources, timePrecisions,
  }
}
